import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import {
  connectRefinementList,
  RefinementListProvided,
} from 'react-instantsearch-core';
import { useDispatch, useSelector } from 'react-redux';
import InstantSearchProvider, {
  useInstantSearch,
} from '../InstantSearchProvider';
import ListViewLogger from '../ListViewLogger';
import {
  PRODUCT_SEARCH_INDEX_BRAND,
  PRODUCT_SEARCH_INDEX_CREATED_AT_DESC,
  PRODUCT_SEARCH_INDEX_FEATURED,
  PRODUCT_SEARCH_INDEX_PRICE_ASC,
  PRODUCT_SEARCH_INDEX_PRICE_DESC,
  PRODUCT_SEARCH_INDEX_WHOLESALE_RATE_ASC,
  PRODUCT_SEARCH_INDEX_WHOLESALE_RATE_DESC,
} from '~constants/search';
import { authUserSelector } from '~redux/auth/selectors/authUser';
import { selectDirectContactOwners } from '~redux/contact';
import { RootState } from '~redux/reducer';
import {
  clearedProducts,
  clearedProductSearchState,
  ProductSearchSortType,
  recievedNbHits,
  recievedProducts,
  searchedProducts,
  selectProductListFilterValueStrings,
  selectProductListSortType,
  selectProductListOffset,
  initializedProductSearchList,
  selectProducts,
  selectProductListPage,
  selectProductListFilters,
  selectProductListCategory,
  selectProductListSubCategory,
  selectProductListQuery,
  ProductListState,
  selectProductListBrandId,
  selectIsProductListInitialized,
  selectProductListGroupByBrand,
  selectNewProducts,
  selectProductListRefinementAttributes,
  recievedRefinementItems,
  makeSelectProductListFiltersByAttribute,
  selectIsPreview,
} from '~redux/search/product';
import { ProductReadModel } from '~types/api';
import { getPersonalizedProducts } from '~usecases/buyer/search/product';
import { deepEquals } from '~utils/compare';
import { useFlag } from '~view/contexts/FlagsContext';

const ProductSearchIndex: Record<ProductSearchSortType, string> = {
  recommended: PRODUCT_SEARCH_INDEX_FEATURED,
  brandRecommended: PRODUCT_SEARCH_INDEX_BRAND,
  newest: PRODUCT_SEARCH_INDEX_CREATED_AT_DESC,
  highestPrice: PRODUCT_SEARCH_INDEX_PRICE_DESC,
  lowestPrice: PRODUCT_SEARCH_INDEX_PRICE_ASC,
  highestWholesaleRate: PRODUCT_SEARCH_INDEX_WHOLESALE_RATE_DESC,
  lowestWholesaleRate: PRODUCT_SEARCH_INDEX_WHOLESALE_RATE_ASC,
};

export const useProductSearchSelector = <TSelected,>(
  selector: (state: RootState, listId: string) => TSelected,
  listId: string,
  compare?: (a: TSelected, b: TSelected) => boolean
) => {
  const memoizedSelector = useMemo(() => selector, []);
  return useSelector(
    (state: RootState) => memoizedSelector(state, listId),
    compare
  );
};

export const productEquals = (a: ProductReadModel[], b: ProductReadModel[]) =>
  JSON.stringify(a.map((product) => product.id)) ===
  JSON.stringify(b.map((product) => product.id));

export const useSelectProducts = (listId: string) =>
  useProductSearchSelector(selectProducts, listId, productEquals);

const ProductSearchLog: React.FC<{ listId: string }> = React.memo(
  ({ listId }) => {
    const auth = useSelector(authUserSelector);
    const items = useSelectProducts(listId);
    const page = useProductSearchSelector(selectProductListPage, listId);
    const sort = useProductSearchSelector(selectProductListSortType, listId);
    const filters = useProductSearchSelector(
      selectProductListFilters,
      listId,
      (a, b) => JSON.stringify(a) === JSON.stringify(b)
    );
    const category = useProductSearchSelector(
      selectProductListCategory,
      listId
    );
    const subCategory = useProductSearchSelector(
      selectProductListSubCategory,
      listId
    );
    const query = useProductSearchSelector(selectProductListQuery, listId);

    return (
      <ListViewLogger
        listId={listId}
        products={items}
        index={(page - 1) * items.length}
        user={auth ?? undefined}
        search_type="product"
        keywords={query?.replaceAll('　', ' ').split(' ')}
        brand={items?.[0]?.brand_name!}
        category={category}
        subCategory={subCategory}
        sorts={[
          {
            type: sort,
            value: [
              'recommended',
              'brandRecommended',
              'newest',
              'highestPrice',
              'highestPrice',
            ].includes(sort)
              ? 'desc'
              : 'asc',
          },
        ]}
        filters={
          filters
            ? Object.entries(filters).flatMap(
                ([type, values]) =>
                  values?.map((value) => ({ type, value })) ?? []
              )
            : undefined
        }
      />
    );
  }
);

const ProductSearchResultsProvider: React.FC<{
  listId: string;
  personalize: boolean;
}> = React.memo(({ listId, personalize, children }) => {
  const dispatch = useDispatch();
  const auth = useSelector(authUserSelector);
  const sort = useProductSearchSelector(selectProductListSortType, listId);
  const {
    hits,
    searching,
    isSearchStalled,
    searchResults,
  } = useInstantSearch<ProductReadModel>();
  const productListSort = useFlag<'default' | 'conversion' | 'personalize'>(
    'productListSort',
    'default'
  );

  useEffect(() => {
    if (searching || isSearchStalled) {
      dispatch(searchedProducts(listId));
      return () => {
        dispatch(clearedProducts(listId));
      };
    }
    if (
      !auth ||
      sort !== 'recommended' ||
      !hits.length ||
      !personalize ||
      !['personalize', 'hybrid'].includes(productListSort)
    ) {
      dispatch(recievedProducts({ listId, products: hits }));
      return;
    }
    dispatch(
      getPersonalizedProducts({
        accountId: auth.attributes.sub,
        listId,
        products: hits,
      })
    );
  }, [auth, searching, isSearchStalled, productListSort, personalize]);

  useEffect(() => {
    if (!searchResults) {
      return;
    }
    dispatch(recievedNbHits({ listId, nbHits: searchResults.nbHits }));
  }, [searchResults?.nbHits]);

  return <>{children}</>;
});

const RefinementList = React.memo(
  connectRefinementList(
    ({
      listId,
      attribute,
      items,
      refine,
      searchForItems,
    }: PropsWithChildren<
      RefinementListProvided & { listId: string; attribute: string }
    >) => {
      const dispatch = useDispatch();
      const labels = useMemo(() => items.map((item) => item.label), [items]);
      const filters = useProductSearchSelector(
        makeSelectProductListFiltersByAttribute(attribute),
        listId,
        deepEquals
      );

      useEffect(() => {
        dispatch(recievedRefinementItems({ listId, attribute, items: labels }));
      }, [listId, attribute, JSON.stringify(labels)]);

      useEffect(() => {
        refine(filters);
      }, [filters]);

      return null;
    }
  )
);

const ProductSearchListProvider: React.FC<{
  listId: string;
  personalize: boolean;
}> = React.memo(({ listId, personalize, children }) => {
  const directOwners = useSelector(selectDirectContactOwners, deepEquals);
  const sort = useProductSearchSelector(selectProductListSortType, listId);
  const indexName = useMemo(() => ProductSearchIndex[sort ?? 'recommended'], [
    sort,
  ]);
  const productFilters = useProductSearchSelector(
    selectProductListFilterValueStrings,
    listId,
    (a, b) => JSON.stringify(a) === JSON.stringify(b)
  );
  const category = useProductSearchSelector(selectProductListCategory, listId);
  const subCategory = useProductSearchSelector(
    selectProductListSubCategory,
    listId
  );
  const brandId = useProductSearchSelector(selectProductListBrandId, listId);
  const query = useProductSearchSelector(selectProductListQuery, listId);
  const isNewProducts = useProductSearchSelector(selectNewProducts, listId);
  const isPreview = useProductSearchSelector(selectIsPreview, listId);
  const filters = useMemo(() => {
    if (isPreview) {
      return `brand_id:'${brandId}'AND suspended:false AND is_published:true OR is_limited:true${
        productFilters.length ? ` AND (${productFilters.join(') AND (')})` : ''
      } AND (product_preorder_shipping_date_timestamp > ${new Date().getTime()} OR product_preorder_shipping_date_timestamp = 0)`;
    }
    return `category_page_id:'${
      brandId
        ? `brands > ${brandId}`
        : category
        ? `products > ${category}`
        : 'products'
    }${subCategory ? ` > ${subCategory}` : ''}'${
      productFilters.length ? ` AND (${productFilters.join(') AND (')})` : ''
    } AND (sell_only_direct:false${
      directOwners?.length
        ? ` OR ${directOwners
            .map((owner) => `product_owner:'${owner}'`)
            .join(' OR ')}`
        : ''
    }) AND (product_preorder_shipping_date_timestamp > ${new Date().getTime()} OR product_preorder_shipping_date_timestamp = 0)${
      isNewProducts
        ? ` AND publishedAtTimestamp >= ${
            new Date().getTime() - 30 * 24 * 60 * 60 * 1000
          }`
        : ''
    }`;
  }, [
    category,
    subCategory,
    brandId,
    isNewProducts,
    productFilters,
    isPreview,
  ]);
  const offset = useProductSearchSelector(selectProductListOffset, listId);
  const auth = useSelector(authUserSelector);
  const attributesToRetrieve = useMemo(() => {
    const attributes = ['*'];
    if (!auth) {
      attributes.push('-product_wholesale_rate');
      attributes.push('-product_sale_wholesale_rate');
      attributes.push('-wholesale_rate');
      attributes.push('-wholesale_price');
      attributes.push('-brand_minimum_buy');
      attributes.push('-brand_additional_minimum_buy');
      attributes.push('-out_of_stock');
    }
    return attributes;
  }, [auth]);
  const groupByBrand = useProductSearchSelector(
    selectProductListGroupByBrand,
    listId
  );
  const refinementAttributes = useProductSearchSelector(
    selectProductListRefinementAttributes,
    listId
  );

  return (
    <InstantSearchProvider
      indexName={indexName}
      query={query}
      filters={filters}
      offset={offset}
      length={500}
      attributesToRetrieve={attributesToRetrieve}
      distinct={groupByBrand}
    >
      <ProductSearchResultsProvider listId={listId} personalize={personalize}>
        {children}
      </ProductSearchResultsProvider>
      {refinementAttributes.map((attribute) => (
        <RefinementList key={attribute} listId={listId} attribute={attribute} />
      ))}
    </InstantSearchProvider>
  );
});

type ProductSearchProviderProps = {
  listId: string;
  personalize?: boolean;
} & Partial<ProductListState>;

const ProductSearchProvider: React.FC<ProductSearchProviderProps> = React.memo(
  ({ listId, children, personalize = false, ...props }) => {
    const dispatch = useDispatch();
    const isInitialized = useProductSearchSelector(
      selectIsProductListInitialized,
      listId
    );

    useEffect(() => {
      dispatch(
        initializedProductSearchList({
          listId,
          ...props,
        })
      );
      return () => {
        dispatch(clearedProductSearchState(listId));
      };
    }, [listId]);

    if (!isInitialized) {
      return null;
    }

    return (
      <ProductSearchListProvider personalize={personalize} listId={listId}>
        {children}
        <ProductSearchLog listId={listId} />
      </ProductSearchListProvider>
    );
  }
);

export default ProductSearchProvider;
